import React, { useState, useEffect, useRef } from "react";
import "./style/visitorForm.scss";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../store/store";
import { clearAuth } from "../../store/slices/authSlice";
import { useForm } from "react-hook-form";
import { Form, Button } from "react-bootstrap";
// import Select from "react-select";
import { ClipLoader } from "react-spinners";
import { useQuery, useMutation } from "react-query";
import { touchlessService } from "../../services/api/touchlessService";
import {
  deleteAllCookies,
  DESTINATION,
  PARKING_LOT,
  POINT_OF_ENTRY,
} from "../../utils/helpers";
// import type { Host } from "../../components/common/HostDropdown/hostSelector";
import type { PreRegisterResponse } from "../../types/preRegistration";
import {
  isRepeatCheckinCycle,
  buildRepeatCheckinVisitorInfo,
} from "../../utils/repeatCheckin";
import {
  type OrgConfig,
  type LocationItem,
  type VisitorType,
  type VisitorTypeField,
  type VisitorTypeDocInfo,
} from "../../types/orgConfig";
// import HostSelector from "../../components/common/HostDropdown/hostSelector";
import ConfirmationModal from "../../components/common/ConfirmationModal/ConfirmationModal";
import { showError } from "../../services/toastservice/toastService";
import { BackButton } from "../../components/ui/Button/backButton";
import renderFields from "./renderFields";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useVisitorData } from "../../hooks/useVisitorData";
import { ROUTES } from "../../utils/constants";
import { useStrings } from "../../hooks/useStrings";
import { shouldShowIdCapture } from "../../utils/idCapture";
import { resolveVideoSkip, shouldShowVideo } from "../../utils/videoStep";

export interface VisitorFormData {
  fullName: string;
  email: string;
  companyName: string;
  phoneNumber: string;
  hostUserId: string;
  hostName: string;
  poeId: string;
  poeName: string;
  buildingId: string;
  buildingName: string;
  parkingLotId: string;
  parkingLotName: string;
  preregisterVisitCustomFieldModels?: Array<{
    orgCustomFieldId: string;
    value: string;
  }>;
  visitCustomFieldsModels?: Array<{
    orgCustomFieldId: string;
    value: string;
  }>;
  [key: string]: unknown;
}

const VisitorForm: React.FC = () => {
  const S = useStrings();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  // const formRef = useRef<HTMLFormElement>(null);

  const {
    visitorinfo,
    updateVisitorInfo,
    visitorTypeId,
    visitorTypeInfo: visitorTypeFieldFiltered,
    preregisterId: preRegisterId,
    visitorFullName: fullName,
    formData,
    preFillNew: prefillNew,
    isPrefill,
    flowInfoFlags,
    videoUrl,
    updateVisitorTypeId,
    saveVideoUrl,
    saveOrgTemplateIds,
    updateVisitorFullName,
    updateVisitorTypeInfo,
    updateFlowInfoFlags,
    updateIdValidationSkip,
    updateVideoSkip,

    updatePreFillNew,
    updatePreFillData,
    updatePreregisterId,
    updateRepeatCheckin,
  } = useVisitorData();

  // Access global config and theme from Redux layer instead of local parsed state
  const { config, themeColors } = useAppConfig();
  const orgConfig = config as OrgConfig;
  // Derived from themeColors via Redux Hook
  const btnColor = themeColors.btnColor;

  const [orgTemplateId, setOrgTemplateId] = useState(() =>
    visitorTypeFieldFiltered ? visitorTypeFieldFiltered?.orgTemplateId : "",
  );

  const [visitorTypeAllFields, setVisitorTypeAllFields] = useState<
    VisitorTypeField[]
  >(() => {
    return visitorTypeFieldFiltered?.fields
      ? visitorTypeFieldFiltered.fields.filter(
          (item: VisitorTypeField) => item.status === "ACTIVE",
        )
      : [];
  });

  // Other component state

  // Pagination state==============================
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [startPoint, setStartPoint] = useState(0);
  const [endPoint, setEndPoint] = useState(5);
  const [docExpiryInDays, setDocExpiryInDays] = useState(0);

  // EN-4518: the ID-reuse result, resolved once per submit. Held in a ref as well
  // as Redux because documentExpiryMutation.onSuccess is created at render time —
  // reading Redux there would see the value from before this submit dispatched it.
  const idValidationSkipRef = useRef<boolean>(false);
  // Covers the checkIdValidationExpiry round trip so the visitor cannot submit twice.
  const [isCheckingIdValidation, setIsCheckingIdValidation] = useState(false);
  // EN-4854: same two-part pattern for the safety-video reuse result.
  const videoSkipRef = useRef<boolean>(false);
  const [isCheckingVideoExpiry, setIsCheckingVideoExpiry] = useState(false);

  // Modal state for confirmation modal
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  // Advanced location fields for Sony POC
  const [dropdownOptionsForSonyFields] = useState<{
    [key: string]: Array<{
      label: string;
      value: string;
      availableSpots?: string;
      totalSpots?: string;
    }>;
  }>({
    [POINT_OF_ENTRY]:
      orgConfig?.pointsOfEntry?.map((item: LocationItem) => ({
        label: item.name,
        value: item.id,
      })) || [],
    [PARKING_LOT]:
      orgConfig?.parkingLots?.map((item: LocationItem) => ({
        label: item.name,
        value: item.id,
        availableSpots: item.availableSpots,
        totalSpots: item.numSpots,
      })) || [],
    [DESTINATION]:
      orgConfig?.buildings?.map((item: LocationItem) => ({
        label: item.name,
        value: item.id,
      })) || [],
  });
  const advanceLocationFields = [
    POINT_OF_ENTRY,
    PARKING_LOT,
    DESTINATION,
    "poeId",
    "buildingId",
    "parkingLotId",
  ];
  const notNeededFeature = false;

  const visitorInfoHasData = useRef<boolean>(false);

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { isValid },
    trigger,
    reset,
  } = useForm<VisitorFormData>({
    defaultValues: {
      fullName: "",
      email: "",
      companyName: "",
      phoneNumber: "",
      hostUserId: "",
      hostName: "",
      poeId: "",
      poeName: "",
      buildingId: "",
      buildingName: "",
      parkingLotId: "",
      parkingLotName: "",
      preregisterVisitCustomFieldModels: [],
      visitCustomFieldModels: [],
    },
    mode: "onChange",
    // reValidateMode: "onBlur",
  });

  const watchedValues = watch();

  // Fetching pre-register data by preRegisterId when preRegisterId is available
  const { data: preRegisterData, isLoading: isLoadingPreRegister } =
    useQuery<PreRegisterResponse>(
      ["preRegister", preRegisterId],
      () =>
        touchlessService.getPreRegisterById(
          preRegisterId!,
        ) as Promise<PreRegisterResponse>,
      {
        enabled:
          !!preRegisterId &&
          !touchlessService.isNullUndefinedOrBlank(preRegisterId) &&
          !isPrefill,
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
          // Handle pre-register data - map to form fields
          if (data) {
            reset({
              fullName: data.fullName || "",
              email: data.email || "",
              companyName: data.companyName || "",
              phoneNumber: data.phoneNumber || "", // API returns phoneNumber, not phone
              hostUserId: data.hostUserId || "",
              hostName: data.hostName || "",
              poeId: data.poeId || "",
              poeName: data.poeName || "",
              buildingId: data.buildingId || "",
              buildingName: data.buildingName || "",
              parkingLotId: data.parkingLotId || "",
              parkingLotName: data.parkingLotName || "",
              preregisterVisitCustomFieldModels:
                data.preregisterVisitCustomFieldModels || [],
              siteName: data?.siteName || "",
              visitorType: data?.visitorType || "",
            });
          }
          updateVisitorTypeId(data.visitorTypeId);
          getAllVisitorInfo();

          // EN-4557 cycle 2+ of a multi check-in invitation. The pre-arrival DRAFT is gone --
          // the first check-in flipped that same row to APPROVED -- but the invitation is still
          // open and the server handed us the previous cycle. Reuse it and go straight to
          // confirmation, so the visitor is not asked again for anything they already gave.
          //
          // MUST NOT set preFillNew: that means "PATCH the DRAFT", and a PATCH against a
          // completed cycle is refused with 409 cycle_already_completed. This path POSTs, and
          // confirmation.tsx stamps referenceVisitId so the server carries the photo, the signed
          // documents, the video stamp and the ID forward from that previous cycle.
          if (isRepeatCheckinCycle(data)) {
            const latest = data.latestVisitInfoModel!;
            updateVisitorFullName(latest.fullName ?? (data.fullName as string) ?? "");
            updateVisitorInfo(buildRepeatCheckinVisitorInfo(latest, data));
            updateRepeatCheckin({
              sourceVisitId: latest.id,
              preRegisterVisitId: data.id,
              sourceSiteId: latest.siteId,
            });
            navigate("/confirmation", { replace: true });
            return;
          }

          if (!touchlessService.isNullUndefinedOrBlank(data?.visitInfoModel)) {
            updateVisitorFullName(data.fullName as string);
            // NOTE: Do NOT call updateIsPrefill(true) here. This query only runs for the
            // contactless ?id= flow (enabled: !isPrefill). Setting isPrefill=true here
            // causes the prefill confirmation screen to show instead of the contactless
            // screen. isPrefill should only be set in preregister.tsx when ?token= is present.
            // updateIsPrefill(true);
            updatePreFillNew(true);
            updatePreFillData(data?.visitInfoModel);
            updateVisitorInfo(data as unknown as Record<string, unknown>);
            navigate("/confirmation", { replace: true });
          }
          trigger();
        },
        onError: () => {
          // console.error("Error fetching pre-register data:", error);
          sessionStorage.setItem("error401", "404");
          sessionStorage.removeItem("preregisterId");
          updatePreregisterId(null);
          navigate("/home");
        },
      },
    );

  // Replace both getByVisitorTypeId API calls with an orgConfig lookup.
  // The orgConfig already contains the full VisitorType objects — identical
  // to what GET /visitortypes/:id would return — so no network round-trip
  // is needed. This mirrors the pattern used in useVisitorData.ts:72.
  useEffect(() => {
    if (!visitorTypeId) {
      console.warn("[VisitorForm] Early return: visitorTypeId is not set");
      return;
    }
    const visitorTypeData = orgConfig?.visitorType?.find(
      (vt) => vt.id === visitorTypeId,
    );
    if (!visitorTypeData) {
      console.warn("[VisitorForm] Early return: visitorTypeId", visitorTypeId, "not found in orgConfig.visitorType", orgConfig?.visitorType?.map((vt) => vt.id));
      return;
    }

    const isContactlessFlow =
      touchlessService.isNullUndefinedOrBlank(preRegisterId) && !isPrefill;
    const isPreRegFlow =
      !touchlessService.isNullUndefinedOrBlank(preRegisterId) &&
      !!preRegisterData;

    if (isContactlessFlow) {
      // Exact onSuccess logic from the former Query 1
      if (!touchlessService.isNullUndefinedOrBlank(visitorTypeData?.videoUrl)) {
        saveVideoUrl(visitorTypeData?.videoUrl as string);
      }
      const oldFields = visitorTypeData.fields.map((k) => ({ ...k }));
      oldFields.forEach((k) => {
        if (k.name === "Full Name") {
          k.fid = "fullName";
          k.value = watchedValues.fullName;
        } else if (k.name === "Email") {
          k.fid = "email";
          k.value = watchedValues.email;
        } else if (k.name === "Host") {
          k.fid = "hostName";
          k.value = watchedValues.hostName;
        } else if (k.name === "Company Name") {
          k.fid = "companyName";
          k.value = watchedValues.companyName;
        } else if (k.name === "Phone Number") {
          k.fid = "phoneNumber";
          k.value = watchedValues?.phoneNumber;
        } else if (k.name === POINT_OF_ENTRY) {
          k.fid = "poeId";
          k.value = watchedValues?.poeId;
        } else if (k.name === DESTINATION) {
          k.fid = "buildingId";
          k.value = watchedValues?.buildingId;
        } else if (k.name === PARKING_LOT) {
          k.fid = "parkingLotId";
          k.value = watchedValues?.parkingLotId;
        } else if (k.orgCustomFieldId) {
          // Custom fields use orgCustomFieldId as fid
          k.fid = k.orgCustomFieldId;
          watchedValues.preregisterVisitCustomFieldModels?.find(
            (preregisterCustomField) => {
              if (
                preregisterCustomField.orgCustomFieldId === k.orgCustomFieldId
              ) {
                k.value = preregisterCustomField.value;
              }
            },
          );
        } else {
          // Fallback: use field id or name as fid
          k.fid = k.id || k.name;
        }
      });
      setVisitorTypeAllFields(
        oldFields.filter((item) => item.status === "ACTIVE"),
      );
      updateVisitorTypeInfo(visitorTypeData);
    }

    if (isPreRegFlow) {
      // Exact onSuccess logic from the former Query 2 (pre-reg flow)
      if (!touchlessService.isNullUndefinedOrBlank(visitorTypeData.videoUrl)) {
        saveVideoUrl(visitorTypeData.videoUrl as string);
      }
      const oldFields = visitorTypeData.fields.map((k) => ({ ...k }));
      oldFields.forEach((k) => {
        if (k.name === "Full Name") {
          k.fid = "fullName";
          k.value = preRegisterData!.fullName || "";
        } else if (k.name === "Email") {
          k.fid = "email";
          k.value = preRegisterData!.email || "";
        } else if (k.name === "Host") {
          k.fid = "hostName";
          k.value = preRegisterData!.hostName || "";
        } else if (k.name === "Company Name") {
          k.fid = "companyName";
          k.value = preRegisterData!.companyName || "";
        } else if (k.name === "Phone Number") {
          k.fid = "phoneNumber";
          k.value = preRegisterData!.phoneNumber || "";
        } else if (k.name === POINT_OF_ENTRY) {
          k.fid = "poeId";
          k.value = preRegisterData!.poeId || "";
        } else if (k.name === DESTINATION) {
          k.fid = "buildingId";
          k.value = preRegisterData!.buildingId || "";
        } else if (k.name === PARKING_LOT) {
          k.fid = "parkingLotId";
          k.value = preRegisterData!.parkingLotId || "";
        } else if (k.orgCustomFieldId) {
          // Custom fields use orgCustomFieldId as fid
          k.fid = k.orgCustomFieldId;
          preRegisterData!.preregisterVisitCustomFieldModels?.find(
            (preregisterCustomField) => {
              if (
                preregisterCustomField.orgCustomFieldId === k.orgCustomFieldId
              ) {
                k.value = preregisterCustomField.value;
              }
            },
          );
        } else {
          // Fallback: use field id or name as fid
          k.fid = k.id || k.name;
        }
      });
      setVisitorTypeAllFields(
        oldFields.filter((item) => item.status === "ACTIVE"),
      );
      updateVisitorTypeInfo(visitorTypeData);

      // Reset form with preRegisterData AFTER fields are set
      reset({
        fullName: preRegisterData!.fullName || "",
        email: preRegisterData!.email || "",
        companyName: preRegisterData!.companyName || "",
        phoneNumber: preRegisterData!.phoneNumber || "",
        hostUserId: preRegisterData!.hostUserId || "",
        hostName: preRegisterData!.hostName || "",
        poeId: preRegisterData!.poeId || "",
        poeName: preRegisterData!.poeName || "",
        buildingId: preRegisterData!.buildingId || "",
        buildingName: preRegisterData!.buildingName || "",
        parkingLotId: preRegisterData!.parkingLotId || "",
        parkingLotName: preRegisterData!.parkingLotName || "",
        preregisterVisitCustomFieldModels:
          preRegisterData!.preregisterVisitCustomFieldModels || [],
      });
      if (
        preRegisterData!.preregisterVisitCustomFieldModels &&
        preRegisterData!.preregisterVisitCustomFieldModels.length > 0
      ) {
        preRegisterData!.preregisterVisitCustomFieldModels.forEach(
          (item: { orgCustomFieldId: string; value: string }) => {
            setValue(item.orgCustomFieldId, item.value);
          },
        );
      }
      if (!fullName || fullName === "") {
        updateVisitorFullName(preRegisterData!.fullName || "");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorTypeId, preRegisterId, isPrefill, preRegisterData, orgConfig]);
  // Intentionally omitted from deps: watchedValues fields (adding them would re-run the
  // effect on every keystroke), and stable dispatch wrappers (reset, setValue,
  // saveVideoUrl, updateVisitorTypeInfo, updateVisitorFullName, fullName).

  // Mutation for document expiry check
  const documentExpiryMutation = useMutation(
    (payload: {
      fullName: string;
      email: string;
      companyName: string;
      phoneNumber: string;
      hostUserId: string;
      visitorTypeId: string;
      docTemplateId: string;
      orgId: string;
      siteId: string;
      checkinMethod: string;
    }) => touchlessService.postDocumentExpiry(payload),
    {
      onSuccess: (data) => {
        const isDocExpired = data.isDocExpired;
        if (isDocExpired) {
          navigate("/signdocument");
        } else {
          if (
            shouldShowIdCapture(
              flowInfoFlags?.isIdCapturingFlag,
              idValidationSkipRef.current,
            )
          ) {
            navigate("/capturepicture");
          } else if (flowInfoFlags?.visitorPhotoFlag) {
            navigate("/visitorpicture");
          } else {
            if (shouldShowVideo(videoUrl, videoSkipRef.current)) {
              navigate("/visitor-video");
            } else {
              navigate("/confirmation", { replace: true });
            }
          }
        }
      },
      onError: (error) => {
        console.error("Document expiry check failed:", error);
        showError(S.SIGN_DOCUMENT.ERROR_DOCUMENT_EXPIRY);
      },
    },
  );

  // Only side effects that can't be done synchronously
  useEffect(() => {
    getAllVisitorInfo();

    // Delay trigger to next tick after all setValue calls
    const timeoutId = setTimeout(() => {
      if (isPrefill || visitorInfoHasData.current) {
        visitorInfoHasData.current = false;
        trigger(); // This helps to validate the form after all the setValue calls are done
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!visitorTypeFieldFiltered) return;

    if (
      flowInfoFlags?.visitorPhotoFlag !== visitorTypeFieldFiltered.photoFlag ||
      flowInfoFlags?.isIdCapturingFlag !==
        visitorTypeFieldFiltered.isIdCapturingEnabled
    ) {
      updateFlowInfoFlags({
        visitorPhotoFlag: visitorTypeFieldFiltered.photoFlag,
        isIdCapturingFlag: visitorTypeFieldFiltered.isIdCapturingEnabled,
      });
    }

    setOrgTemplateId(
      visitorTypeFieldFiltered ? visitorTypeFieldFiltered.orgTemplateId : "",
    );
  }, [visitorTypeFieldFiltered]);

  const getAllVisitorInfo = () => {
    if (orgConfig) {
      if (visitorTypeFieldFiltered) {
        const visitorType: VisitorType = visitorTypeFieldFiltered;
        // Set form values from session storage into react hook form
        setValue("fullName", fullName);
        setValue("hostName", formData?.hostName || "");
        setValue("hostUserId", formData?.hostId || "");
        const visitorInfoTemp: VisitorFormData = (visitorinfo ||
          JSON.parse("{}")) as VisitorFormData;
        if (visitorInfoTemp && Object.keys(visitorInfoTemp).length > 0) {
          Object.entries(visitorInfoTemp).forEach(([key, value]) => {
            if (
              key === "visitCustomFieldModels" ||
              key === "preregisterVisitCustomFieldModels"
            ) {
              (value as { orgCustomFieldId: string; value: string }[]).forEach(
                (item) => {
                  setValue(item.orgCustomFieldId, item.value, {
                    shouldValidate: true,
                  });
                },
              );
            } else {
              setValue(key, value, { shouldValidate: true });
            }
          });
          // trigger();
          visitorInfoHasData.current = true;
        }
        // Handle document templates
        if (
          visitorType.visitorTypeDocsInfos &&
          visitorType.visitorTypeDocsInfos.length > 0
        ) {
          const templateIds = visitorType.visitorTypeDocsInfos.map(
            (x: VisitorTypeDocInfo) => x.orgDocTemplateId,
          );
          saveOrgTemplateIds(templateIds);
          setDocExpiryInDays(visitorType.docExpiryInDays || 0);
        }

        // Map field IDs and values (same logic as Angular)
        const mappedFields = visitorTypeAllFields.map(
          (field: VisitorTypeField) => {
            const mappedField = { ...field };

            if (field.name === "Full Name") {
              mappedField.fid = "fullName";
              mappedField.value = watchedValues.fullName || "";
            } else if (field.name === "Email") {
              mappedField.fid = "email";
              mappedField.value = watchedValues.email || "";
            } else if (field.name === "Host") {
              mappedField.fid = "hostName";
              mappedField.value = watchedValues.hostName || "";
            } else if (field.name === "Company Name") {
              mappedField.fid = "companyName";
              mappedField.value = watchedValues.companyName || "";
            } else if (field.name === "Phone Number") {
              mappedField.fid = "phoneNumber";
              mappedField.value = watchedValues.phoneNumber || "";
            } else if (field.name === POINT_OF_ENTRY) {
              mappedField.fid = "poeId";
              mappedField.value = watchedValues.poeId;
            } else if (field.name === DESTINATION) {
              mappedField.fid = "buildingId";
              mappedField.value = watchedValues.buildingId;
            } else if (field.name === PARKING_LOT) {
              mappedField.fid = "parkingLotId";
              mappedField.value = watchedValues.parkingLotId;
            } else if (field.orgCustomFieldId) {
              // Custom fields use orgCustomFieldId as fid
              mappedField.fid = field.orgCustomFieldId;
            } else {
              // Fallback: use field id or name as fid
              mappedField.fid = field.id || field.name;
            }

            return mappedField;
          },
        );

        setVisitorTypeAllFields(
          mappedFields.filter((item) => item.status === "ACTIVE"),
        );
      } else {
        console.debug("Visitor type not found");
      }
    }
  };

  // EN-4518: decide whether this visitor's previously scanned ID can be reused.
  // Returns true only on an explicit isIdValidationExpired === false. Every other
  // outcome returns false so the ID step is shown — a missing or malformed field
  // must never be read as "skip the scan".
  const resolveIdValidationSkip = async (
    data: VisitorFormData,
    visitorType: VisitorType | undefined,
  ): Promise<boolean> => {
    const idValidityDays = Number(visitorType?.idValidityDays) || 0;

    // Mirrors the docExpiryInDays <= 0 guard below: 0 days means reuse is turned
    // off for this visitor type, so there is nothing to ask the backend.
    if (!flowInfoFlags?.isIdCapturingFlag || idValidityDays <= 0) {
      return false;
    }

    try {
      setIsCheckingIdValidation(true);
      const response = await touchlessService.postIdValidationExpiry({
        visitorTypeId: visitorTypeId || "",
        fullName: data.fullName,
        email: data.email,
        siteId: orgConfig.siteId,
      });
      return response?.isIdValidationExpired === false;
    } catch (error) {
      // Fail safe: a failed check means the visitor scans their ID as normal.
      console.error("ID validation expiry check failed:", error);
      return false;
    } finally {
      setIsCheckingIdValidation(false);
    }
  };

  // EN-4854: may this visitor skip the safety video? The rules (window off, no
  // video, fail-safe on error) live in resolveVideoSkip; this only owns the
  // loading flag and builds the request.
  const checkVideoSkip = async (
    data: VisitorFormData,
    visitorType: VisitorType | undefined,
  ): Promise<boolean> => {
    setIsCheckingVideoExpiry(true);
    try {
      return await resolveVideoSkip(
        visitorType,
        {
          visitorTypeId: visitorTypeId || "",
          fullName: data.fullName,
          email: data.email,
          siteId: orgConfig.siteId,
        },
        (payload) => touchlessService.postVideoExpiry(payload),
      );
    } finally {
      setIsCheckingVideoExpiry(false);
    }
  };

  const onSubmit = async (data: VisitorFormData) => {
    if (!checkValidation(data)) {
      return;
    }

    // Guard against a null `orgConfig` (Redux state was reset mid-flow, e.g.
    // after a cancel handler wiped sessionStorage, or the initial
    // /visitor/orgconfig call failed). Several lines below dereference
    // `orgConfig.siteName`, `.visitorType`, `.orgId`, `.siteId` without
    // optional chaining — if orgConfig is null the form crashes with
    // "Cannot read properties of null (reading 'siteName')" and the visitor
    // sees a hard error. Bail out early with a clear message instead.
    if (!orgConfig) {
      console.error("VisitorForm.onSubmit: orgConfig is null, cannot submit");
      showError(S.COMMON.SESSION_EXPIRED);
      return;
    }

    // Map custom field values from form data into preregisterVisitCustomFieldModels
    const customFieldModels = visitorTypeAllFields
      .filter((field) => field.orgCustomFieldId && field.status === "ACTIVE")
      .map((field) => ({
        visitTypeFieldId: field.id,
        name: field.name,
        orgCustomFieldId: field.orgCustomFieldId as string,
        value: String(data[field.orgCustomFieldId as string] || ""),
      }));

    // Remove any custom field values that are present in data because we have moved them into customFieldModels now for the request
    visitorTypeAllFields
      .filter((field) => field.orgCustomFieldId && field.status === "ACTIVE")
      .map((field) => field.orgCustomFieldId)
      .forEach((id) => {
        if (typeof id === "string" && data[id]) {
          delete data[id];
        }
      });

    if (touchlessService.isNullUndefinedOrBlank(data?.siteName)) {
      data.siteName = orgConfig?.siteName ?? "";
    }
    if (touchlessService.isNullUndefinedOrBlank(data?.visitorTypeId)) {
      data.visitorType =
        orgConfig?.visitorType?.find((item) => item.id === visitorTypeId)
          ?.visitorType || "";
    }

    data.visitCustomFieldModels = customFieldModels;

    //  Remove any empty values from data
    Object.keys(data).forEach((key) => {
      if (data[key] === "" || data[key] === null || data[key] === undefined) {
        delete data[key];
      }
    });

    const visitorInfo = {
      ...data,
    };

    updateVisitorInfo(visitorInfo);

    const visitorTypeData =
      orgConfig.visitorType?.filter(
        (item) => item.id === visitorTypeId && item.status === "ACTIVE",
      ) || [];

    // EN-4518 / EN-4854: resolve ID and video reuse before any navigation. Doing it
    // here rather than at each navigation site matters because later pages also
    // route into these steps, and by then the form data needed for the request is
    // gone. The two checks are independent, so they run in parallel; each one
    // handles its own errors, so this cannot reject.
    const [idValidationSkip, videoSkip] = await Promise.all([
      resolveIdValidationSkip(data, visitorTypeData[0]),
      checkVideoSkip(data, visitorTypeData[0]),
    ]);
    idValidationSkipRef.current = idValidationSkip;
    updateIdValidationSkip(idValidationSkip);
    videoSkipRef.current = videoSkip;
    updateVideoSkip(videoSkip);

    // Check if document expiry check is needed
    if (
      visitorTypeData[0]?.visitorTypeDocsInfos &&
      visitorTypeData[0]?.visitorTypeDocsInfos.length > 0
    ) {
      if (docExpiryInDays <= 0) {
        navigate("/signdocument");
      } else {
        const payload = {
          fullName: data.fullName,
          email: data.email,
          companyName: data.companyName,
          phoneNumber: data.phoneNumber,
          hostUserId: data.hostUserId,
          visitorTypeId: visitorTypeId || "",
          docTemplateId: orgTemplateId,
          orgId: orgConfig.orgId,
          siteId: orgConfig.siteId,
          checkinMethod: "MOBILEWEB",
        };

        documentExpiryMutation.mutate(payload);
      }
    } else {
      // Navigate based on flags
      if (
        shouldShowIdCapture(flowInfoFlags?.isIdCapturingFlag, idValidationSkip)
      ) {
        navigate("/capturepicture");
      } else if (flowInfoFlags?.visitorPhotoFlag) {
        navigate("/visitorpicture");
      } else {
        // EN-4854: reads the Redux videoUrl like every other site. This used to
        // read a "videoUrl" sessionStorage key that nothing has written since the
        // Redux migration (EN-2858), so the video was never shown on this path.
        if (shouldShowVideo(videoUrl, videoSkip)) {
          navigate("/visitor-video");
        } else {
          navigate("/confirmation", { replace: true });
        }
      }
    }
  };

  const checkValidation = (data: VisitorFormData): boolean => {
    const regexpFullName = new RegExp(/^[\p{L}\p{M}\p{Zs}.''-]+$/u);
    const regexpEmail = new RegExp(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
    for (let key = startPoint; key < endPoint; key++) {
      const field = visitorTypeAllFields[key];
      if (!field) continue;

      // Full Name validation
      if (
        field.fid === "fullName" &&
        field.status === "ACTIVE" &&
        field.setting === "MANDATORY" &&
        data.fullName !== "" &&
        !regexpFullName.test(data.fullName.trim())
      ) {
        showError(S.VALIDATION.FULL_NAME_REQUIRED);
        return false;
      }

      // Email required validation
      if (
        field.fid === "email" &&
        field.status === "ACTIVE" &&
        field.setting === "MANDATORY" &&
        !data.email
      ) {
        showError(S.VALIDATION.EMAIL_REQUIRED);
        return false;
      }

      // Email format validation (runs for both mandatory and non-mandatory fields when a value is present)
      if (
        field.fid === "email" &&
        field.status === "ACTIVE" &&
        data.email &&
        String(data.email).length > 0 &&
        !regexpEmail.test(String(data.email).trim())
      ) {
        showError(S.VALIDATION.EMAIL_INVALID);
        return false;
      }

      // Host validation
      if (
        field.fid === "hostName" &&
        field.status === "ACTIVE" &&
        field.setting === "MANDATORY" &&
        !data.hostUserId
      ) {
        showError(S.VALIDATION.HOST_REQUIRED);
        return false;
      }

      // Company Name validation
      if (
        field.fid === "companyName" &&
        field.status === "ACTIVE" &&
        field.setting === "MANDATORY" &&
        !data.companyName
      ) {
        showError(S.VALIDATION.COMPANY_NAME_REQUIRED);
        return false;
      }

      // Phone Number validation
      if (
        field.fid === "phoneNumber" &&
        field.status === "ACTIVE" &&
        field.setting === "MANDATORY" &&
        !data.phoneNumber
      ) {
        showError(S.VALIDATION.PHONE_NUMBER_REQUIRED);
        return false;
      }

      // Custom field validation
      if (field.orgCustomFieldId) {
        const customFieldValue = data[field.orgCustomFieldId as string];
        if (
          field.status === "ACTIVE" &&
          field.setting === "MANDATORY" &&
          (!customFieldValue || String(customFieldValue).trim() === "")
        ) {
          showError(S.VALIDATION.CUSTOM_FIELD_REQUIRED.replace('{field}', field.displayText || field.name));
          return false;
        }
      }
    }

    return true;
  };

  const onBack = () => {
    if (isPrefill) {
      navigate("/prefill-details");
      return;
    }
    if (preRegisterId) {
      navigate("/pre-reg-confirmation");
      return;
    }
    // Check if HOST field is active — if not, skip select-host entirely
    const hostField = visitorTypeFieldFiltered?.fields?.find(
      (f) => f.fid === "HOST" && f.status === "ACTIVE",
    );
    if (!hostField) {
      const activeVisitorTypes = orgConfig?.visitorType?.filter(
        (vt) => vt.status === "ACTIVE",
      ) ?? [];
      navigate(
        activeVisitorTypes.length > 1
          ? `/${ROUTES.VISITOR_TYPES}`
          : `/${ROUTES.FULL_NAME}`,
      );
      return;
    }
    navigate(`/${ROUTES.SELECT_HOST}`);
  };

  const handleCancel = () => {
    dispatch(clearAuth());
    sessionStorage.clear();
    deleteAllCookies();
    setShowCancelModal(false);
    navigate("/");
  };

  // Pagination functions

  // Calculate total pages
  useEffect(() => {
    if (visitorTypeAllFields && visitorTypeAllFields.length > 0) {
      setTotalPage(Math.ceil(visitorTypeAllFields.length / 5) - 1);
    }
  }, [visitorTypeAllFields]);

  const onBackClick = () => {
    if (currentPage > 0) {
      setEndPoint(currentPage * 5);
      setCurrentPage(currentPage - 1);
      setStartPoint(startPoint - 5);
    }
  };

  const onNextClick = () => {
    const isCurrentPageValid = checkValidation(getValues());
    if (isCurrentPageValid) {
      if (
        visitorTypeAllFields.length > endPoint &&
        visitorTypeAllFields.length > endPoint + 5
      ) {
        setStartPoint(startPoint + 5);
        setEndPoint(endPoint + 5);
      } else {
        setEndPoint(visitorTypeAllFields.length);
        setStartPoint(startPoint + 5);
      }

      if (totalPage > currentPage) {
        setCurrentPage(currentPage + 1);
      }
    }
  };
  // End pagination functions

  function getDropdownOptions(fieldName: string) {
    if (fieldName === "poeId") {
      return dropdownOptionsForSonyFields[POINT_OF_ENTRY];
    } else if (fieldName === "buildingId") {
      return dropdownOptionsForSonyFields[DESTINATION];
    } else if (fieldName === "parkingLotId") {
      return dropdownOptionsForSonyFields[PARKING_LOT];
    }
    return dropdownOptionsForSonyFields[fieldName] || [];
  }

  if (isLoadingPreRegister) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <ClipLoader color={btnColor} size={50} />
      </div>
    );
  }

  return (
    <>
      <div className="main-screen" data-testid="VF-div-main-screen">
        <div
          className="screen-outer screen-visitorform"
          id="visitor-type"
          data-testid="VF-div-screen-outer"
        >
          <div
            data-testid="VF-form-visitor"
          >
            <div
              className="screen-header-fixed"
              data-testid="VF-div-screen-header-fixed"
            >
              <div
                className="header-fixed-left"
                data-testid="VF-div-header-fixed-left"
              >
                {!prefillNew &&
                  (currentPage == 0 ? (
                    <BackButton onBack={onBack} data-testid="VF-button-back" />
                  ) : (
                    <BackButton
                      onBack={onBackClick}
                      data-testid="VF-button-back"
                    />
                  ))}
              </div>
              <div
                className="header-fixed-center"
                data-testid="VF-div-header-fixed-center"
              >
                <h6 data-testid="VF-h6-header-title"></h6>
              </div>
              <div
                className="header-fixed-right"
                data-testid="VF-div-header-fixed-right"
              ></div>
            </div>

            <div className="screen-inner" data-testid="VF-div-screen-inner">
              <div
                className="screen-subinner"
                data-testid="VF-div-screen-subinner"
              >
                <div className="screen-body" data-testid="VF-div-screen-body">
                  <h5 className="screen-title" data-testid="VF-h5-screen-title">
                    {S.VISITOR_FORM.TITLE}
                  </h5>
                  <p
                    className="screen-subtitle"
                    data-testid="VF-p-screen-subtitle"
                  >
                    {S.VISITOR_FORM.SUBTITLE}
                  </p>
                  <div
                    id="visitor-form"
                    className="visitor-form"
                    data-testid="VF-div-visitor-form"
                  >
                    {visitorTypeAllFields
                      .slice(startPoint, endPoint)
                      .map((field, i) => (
                        <div
                          key={
                            field.fid || field.id || `field-${startPoint + i}`
                          }
                          className={`form-row`}
                          data-testid={`VF-div-form-row-${i}`}
                        >
                          {field.status === "ACTIVE" && (
                            <div
                              className="form-group"
                              data-testid={`VF-div-form-group-${i}`}
                            >
                              <p
                                className="que-counter"
                                data-testid={`VF-p-que-counter-${i}`}
                              >
                                {i + 1 + startPoint}/
                                {
                                  visitorTypeAllFields.filter(
                                    (field) => field.status === "ACTIVE",
                                  ).length
                                }
                              </p>
                              <Form.Label
                                className={
                                  field.setting === "MANDATORY"
                                    ? "required-field"
                                    : ""
                                }
                                data-testid={`VF-label-field-${i}`}
                              >
                                {field.displayText || field.name}
                              </Form.Label>

                              {renderFields(
                                field,
                                i,
                                control,
                                watchedValues,
                                setValue,
                                trigger,
                                () => {},
                                () => {},
                                btnColor,
                                dropdownOptionsForSonyFields,
                                advanceLocationFields,
                                notNeededFeature,
                                PARKING_LOT,
                                getDropdownOptions,
                                orgConfig,
                                visitorTypeFieldFiltered?.userGroup?.id || "",
                                S,
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
                <div className="screen-footer footer-flex">
                  {currentPage !== totalPage && (
                    <button
                      type="button"
                      onClick={onNextClick}
                      className="btn btn-primary btn-width-full font-weight-semibold"
                      disabled={!isValid}
                      data-testid="VF-button-continue"
                    >
                      {S.COMMON.CONTINUE}
                    </button>
                  )}
                  {currentPage ===
                    totalPage /* Submit button on last page */ && (
                    <Button
                      type="button"
                      className="btn btn-primary btn-width-full font-weight-semibold"
                      disabled={
                        !isValid ||
                        isCheckingIdValidation ||
                        isCheckingVideoExpiry
                      }
                      onClick={handleSubmit(onSubmit)}
                      data-testid="VF-button-submit"
                    >
                      {S.COMMON.CONTINUE} &nbsp;&nbsp;
                      <i className="bi bi-arrow-right-short"></i>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
        data-testid="VF-modal-confirmation"
      />
    </>
  );
};

export default VisitorForm;
